import { fetcher } from '@data/utils/api/fetcher/fetcher';
import formatDate from '@data/utils/format-date';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

// ── Response schema ────────────────────────────────────────────────────────────

const leafColumnSchema = z.object({
  type: z.enum([
    'string',
    'integer',
    'long',
    'object',
    'decimal',
    'id',
    'double',
    'date',
    'percent',
    'user',
    // A proficiency level's name, e.g. "Proficient".
    'band',
  ]),
  label: z.string(),
  key: z.string(),
  sortable: z.boolean().optional(),
});

const groupColumnSchema = z.object({
  type: z.literal('group'),
  label: z.string(),
  key: z.string().optional(),
  sortable: z.boolean().optional(),
  columns: z.array(leafColumnSchema),
});

const columnSchema = z.union([leafColumnSchema, groupColumnSchema]);

export const metricsTableV2Schema = z.object({
  name: z.string(),
  toolTips: z.string().nullish(),
  page: z.number(),
  pageSize: z.number(),
  totalPageCount: z.number(),
  totalRecords: z.number(),
  columns: z.array(columnSchema),
  rows: z.array(z.array(z.union([z.string(), z.number(), z.null()]))),
});

export type MetricsTableV2Response = z.infer<typeof metricsTableV2Schema>;
export type MetricsTableColumn = z.infer<typeof columnSchema>;

// ── Order by ───────────────────────────────────────────────────────────────────

export type OrderByDirection = 'ASC' | 'DESC';

export interface MetricsTableOrderBy {
  key: string;
  order: OrderByDirection;
}

// ── Request body ───────────────────────────────────────────────────────────────

export enum MetricTableType {
  AllClassOverview = 'ALL_CLASS_OVERVIEW',
  IndividualClassOverview = 'INDIVIDUAL_CLASS_OVERVIEW',
  AllClassLexileAssessments = 'ALL_CLASS_LEXILE_ASSESSMENTS',
  IndividualClassLexileAssessments = 'INDIVIDUAL_CLASS_LEXILE_ASSESSMENTS',
  AllClassGEAssessments = 'ALL_CLASS_GE_ASSESSMENTS',
  IndividualClassGEAssessments = 'INDIVIDUAL_CLASS_GE_ASSESSMENTS',
  EngagementFeed = 'ENGAGEMENT_FEED',
  AllClassSkills = 'ALL_CLASS_SKILLS',
  AllClassSkillGroup = 'ALL_CLASS_SKILL_GROUP',
  IndividualClassSkillGroup = 'INDIVIDUAL_CLASS_SKILL_GROUP',
  IndividualStudentSkills = 'INDIVIDUAL_STUDENT_SKILLS',
}

export interface MetricsTableV2Request {
  orgId: string;
  metricType: MetricTableType;
  teacherIds: string[];
  startDate: Date;
  endDate: Date;
  edApps: string[];
  page: number;
  pageSize: number;
  searchTerms?: string;
  classIds?: string[] | null;
  studentIds?: string[] | null;
  orderBy?: MetricsTableOrderBy[];
  fetchAllRecords?: boolean;
  /** The skill a per-skill table reports on. */
  skillNodeId?: number;
  /** The one student a per-student table reports on. */
  studentId?: string;
}

// ── Hook props ─────────────────────────────────────────────────────────────────

export interface UseMetricsTableV2ApiProps extends MetricsTableV2Request {
  enabled?: boolean;
}

// ── Fetcher ────────────────────────────────────────────────────────────────────

const getMetricsTableV2Api = async ({
  orgId,
  metricType,
  teacherIds,
  startDate,
  endDate,
  edApps,
  page,
  pageSize,
  searchTerms,
  classIds,
  studentIds,
  orderBy,
  fetchAllRecords = false,
  skillNodeId,
  studentId,
}: MetricsTableV2Request) =>
  fetcher(`/metrics-table/${metricType}`, {
    service: '/data',
    version: '/v2',
    method: 'POST',
    body: JSON.stringify({
      orgId,
      metricType,
      teacherIds,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate, { boundary: 'end' }),
      edApps,
      page,
      pageSize,
      searchTerms: searchTerms ?? null,
      classIds: classIds ?? null,
      studentIds: studentIds ?? null,
      orderBy: orderBy ?? null,
      fetchAllRecords,
      ...(skillNodeId != null ? { skillNodeId } : {}),
      ...(studentId ? { studentId } : {}),
    }),
  }).then((res) => metricsTableV2Schema.parse(res));

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useMetricsTableV2Api({
  enabled,
  ...request
}: UseMetricsTableV2ApiProps) {
  const {
    metricType,
    orgId,
    teacherIds,
    startDate,
    endDate,
    edApps,
    page,
    pageSize,
    searchTerms,
    classIds,
    studentIds,
    orderBy,
    fetchAllRecords = false,
    skillNodeId,
    studentId,
  } = request;

  const query = useQuery({
    queryKey: [
      'metrics-table-v2',
      {
        metricType,
        orgId,
        teacherIds,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate, { boundary: 'end' }),
        edApps,
        page,
        pageSize,
        searchTerms: searchTerms ?? null,
        classIds: classIds ?? null,
        studentIds: studentIds ?? null,
        orderBy: orderBy ?? null,
        fetchAllRecords,
        skillNodeId: skillNodeId ?? null,
        studentId: studentId ?? null,
      },
    ],
    queryFn: () => getMetricsTableV2Api(request),
    enabled: enabled !== false,
    staleTime: 3 * 60 * 1000, // 3 Min Stale Time
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    loading: query.isLoading,
    error: query.error,
  };
}
